import {
  AllocationMethod,
  AllocationValueBasis,
  DebtAllocationSnapshot,
  DebtEvent,
  DebtEventType,
  DebtRecord,
  FinancialInputs,
  LoanType,
  Property,
  PropertyDebtLink
} from '../types';
import { calculateMonthlyPayment } from './buildAmortizationSchedule';

const round2 = (value: number) => Math.round(value * 100) / 100;
const todayIso = () => new Date().toISOString().slice(0, 10);
export const DEFAULT_PROPERTY_HOLD_YEARS = 30;
const resolvePropertyRecord = (propertyId: string, properties: Record<string, Property>) => (
  properties[propertyId]
  || Object.values(properties).find((property) => property.supabaseId === propertyId)
  || null
);

const parseIsoDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toIsoDate = (value: Date): string => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const clampPaymentDay = (value?: number | null) => Math.max(1, Math.min(28, Number(value || 1)));

const shiftToPaymentDay = (date: Date, paymentDay: number): Date => (
  new Date(date.getFullYear(), date.getMonth(), Math.min(paymentDay, new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()))
);

const addMonthsWithDay = (date: Date, months: number, paymentDay: number): Date => {
  const shifted = new Date(date.getFullYear(), date.getMonth() + months, 1);
  return shiftToPaymentDay(shifted, paymentDay);
};

const compareIsoDates = (left: string, right: string) => left.localeCompare(right);

const isInterestOnlyStyleLoan = (loanType: LoanType): boolean => (
  loanType === 'interest_only' || loanType === 'line_of_credit'
);

export interface DebtScheduleRow {
  id: string;
  rowType: 'scheduled_payment' | 'extra_principal_payment';
  date: string;
  status: 'past' | 'future';
  beginningBalance: number;
  scheduledPayment: number;
  interestPortion: number;
  principalPortion: number;
  extraPrincipal: number;
  endingBalance: number;
  notes: string;
}

export interface DebtLiveSnapshot {
  debt: DebtRecord;
  scheduleRows: DebtScheduleRow[];
  currentBalance: number;
  scheduledMonthlyPayment: number;
  nextPaymentDate: string | null;
  currentInterestPortion: number;
  currentPrincipalPortion: number;
  remainingTermMonths: number;
  estimatedPayoffDate: string | null;
}

interface BuildDebtSnapshotOptions {
  includeSharedPortfolioDebt?: boolean;
}

interface BuildDebtLiveSnapshotOptions {
  includeScheduleRows?: boolean;
}

type ScheduledDebtScheduleRow = DebtScheduleRow & { rowType: 'scheduled_payment' };

export const inferLoanType = (debt: Partial<DebtRecord>): LoanType => {
  if (debt.loanType) return debt.loanType;
  if (debt.interestOnly) return 'interest_only';
  if (debt.balloonDate && (debt.amortYears || 0) > 0) return 'amortizing_balloon';
  if (debt.balloonDate) return 'balloon';
  return 'amortizing';
};

const getDefaultFirstPaymentDate = (paymentDay: number, baseDate = new Date()) => (
  toIsoDate(addMonthsWithDay(baseDate, 1, paymentDay))
);

export const createDebtRecord = (): DebtRecord => {
  const now = Date.now();
  const paymentDay = new Date().getDate();
  return {
    id: crypto.randomUUID(),
    ownershipEntity: '',
    isSharedPortfolioDebt: false,
    name: 'Portfolio Loan',
    loanType: 'amortizing',
    lender: '',
    originationDate: todayIso(),
    firstPaymentDate: getDefaultFirstPaymentDate(paymentDay),
    paymentDayOfMonth: clampPaymentDay(paymentDay),
    paymentFrequency: 'monthly',
    originalBalance: 0,
    currentBalance: 0,
    interestRate: 0,
    amortYears: 30,
    interestOnly: false,
    interestOnlyMonths: 0,
    assumeScheduledPaymentsMade: true,
    maturityDate: '',
    balloonDate: '',
    balloonAmount: 0,
    creditLimit: 0,
    monthlyPayment: 0,
    notes: '',
    createdAt: now,
    updatedAt: now
  };
};

const getEffectiveOriginalBalance = (debt: Partial<DebtRecord>) => {
  const hasTimingData = Boolean(debt.originationDate || debt.firstPaymentDate);
  if (!hasTimingData && Number(debt.currentBalance || 0) > 0) return Math.max(0, Number(debt.currentBalance || 0));
  return Math.max(0, Number(debt.originalBalance || debt.currentBalance || 0));
};

export const calculateDebtMonthlyPayment = (
  debt: Pick<DebtRecord, 'originalBalance' | 'currentBalance' | 'interestRate' | 'amortYears' | 'interestOnly' | 'interestOnlyMonths' | 'loanType'>
): number => {
  const originalBalance = Math.max(0, debt.originalBalance || debt.currentBalance || 0);
  const currentBalance = Math.max(0, debt.currentBalance || originalBalance);
  const rate = Math.max(0, debt.interestRate || 0);
  const monthlyInterestOnlyEstimate = rate === 0 ? 0 : currentBalance * (rate / 100 / 12);

  if (debt.loanType === 'interest_only' || debt.loanType === 'line_of_credit') return round2(monthlyInterestOnlyEstimate);
  if (debt.loanType === 'balloon' && (debt.amortYears || 0) <= 0) return round2(monthlyInterestOnlyEstimate);
  return round2(calculateMonthlyPayment(originalBalance, rate, Math.max(1, debt.amortYears || 30)));
};

export const normalizeDebtRecord = (debt: Partial<DebtRecord>): DebtRecord => {
  const now = Date.now();
  const paymentDay = clampPaymentDay(
    debt.paymentDayOfMonth ||
    parseIsoDate(debt.firstPaymentDate || '')?.getDate() ||
    parseIsoDate(debt.originationDate || '')?.getDate() ||
    new Date().getDate()
  );
  const originationReference = parseIsoDate(debt.originationDate || '') || new Date();
  const firstPaymentDate = debt.firstPaymentDate || getDefaultFirstPaymentDate(paymentDay, originationReference);
  const originalBalance = getEffectiveOriginalBalance(debt);
  const currentBalance = Math.max(0, Number(debt.currentBalance || originalBalance));
  const normalizedLoanType = inferLoanType(debt);

  const normalized: DebtRecord = {
    id: debt.id || crypto.randomUUID(),
    supabaseId: debt.supabaseId ?? null,
    propertyId: debt.propertyId ?? null,
    ownershipEntity: debt.ownershipEntity ?? '',
    isSharedPortfolioDebt: Boolean(debt.isSharedPortfolioDebt),
    name: debt.name || 'Portfolio Loan',
    loanType: normalizedLoanType,
    lender: debt.lender || '',
    originationDate: debt.originationDate || toIsoDate(originationReference),
    firstPaymentDate,
    paymentDayOfMonth: paymentDay,
    paymentFrequency: 'monthly',
    originalBalance,
    currentBalance,
    interestRate: Math.max(0, Number(debt.interestRate || 0)),
    amortYears: Math.max(0, Number(debt.amortYears || (normalizedLoanType === 'line_of_credit' ? 0 : 30))),
    interestOnly: isInterestOnlyStyleLoan(normalizedLoanType) || (normalizedLoanType === 'custom' ? Boolean(debt.interestOnly) : normalizedLoanType === 'amortizing_balloon'),
    interestOnlyMonths: Math.max(0, Number(debt.interestOnlyMonths || 0)),
    assumeScheduledPaymentsMade: debt.assumeScheduledPaymentsMade !== false,
    maturityDate: debt.maturityDate || '',
    balloonDate: debt.balloonDate || '',
    balloonAmount: Math.max(0, Number(debt.balloonAmount || 0)),
    creditLimit: Math.max(0, Number(debt.creditLimit || 0)),
    monthlyPayment: Math.max(0, Number(debt.monthlyPayment || 0)),
    notes: debt.notes || '',
    createdAt: Number(debt.createdAt || now),
    updatedAt: Number(debt.updatedAt || now)
  };

  normalized.monthlyPayment = calculateDebtMonthlyPayment(normalized);
  return normalized;
};

const getScheduledPaymentAmount = (debt: DebtRecord, balance: number, paymentIndex: number) => {
  const rate = Math.max(0, debt.interestRate || 0);
  const interestOnlyMonthly = rate === 0 ? 0 : balance * (rate / 100 / 12);

  if (debt.loanType === 'line_of_credit') return round2(interestOnlyMonthly);
  if (debt.loanType === 'interest_only') return round2(interestOnlyMonthly);
  if (debt.loanType === 'balloon' && debt.amortYears <= 0) return round2(interestOnlyMonthly);
  if ((debt.loanType === 'amortizing_balloon' || debt.loanType === 'custom') && debt.interestOnly && paymentIndex < (debt.interestOnlyMonths || 0)) {
    return round2(interestOnlyMonthly);
  }

  return round2(debt.monthlyPayment || calculateDebtMonthlyPayment(debt));
};

export const buildDebtLiveSnapshot = (
  sourceDebt: Partial<DebtRecord>,
  events: DebtEvent[] = [],
  asOfDate = new Date(),
  options?: BuildDebtLiveSnapshotOptions
): DebtLiveSnapshot => {
  const includeScheduleRows = options?.includeScheduleRows !== false;
  const debt = normalizeDebtRecord(sourceDebt);
  const relevantEvents = events
    .filter((event) => event.debtId === debt.id && event.type === 'extra_principal_payment' && Number(event.amount || 0) > 0 && parseIsoDate(event.date))
    .sort((left, right) => compareIsoDates(left.date, right.date));
  const paymentDay = clampPaymentDay(debt.paymentDayOfMonth);
  const firstPaymentDate = parseIsoDate(debt.firstPaymentDate) || addMonthsWithDay(new Date(), 1, paymentDay);
  const maxSchedulePayments = Math.max(24, (debt.amortYears || 0) * 12 + Math.max(0, debt.interestOnlyMonths || 0) + 24);
  const today = toIsoDate(asOfDate);

  const scheduleRows: DebtScheduleRow[] = [];
  let balance = Math.max(0, debt.originalBalance || debt.currentBalance || 0);
  let eventIndex = 0;
  let paymentDate = firstPaymentDate;
  let paymentIndex = 0;
  let lastPastEndingBalance = round2(debt.originalBalance);
  let lastPastScheduledPaymentRow: ScheduledDebtScheduleRow | null = null;
  let nextPaymentRow: ScheduledDebtScheduleRow | null = null;
  let remainingTermMonths = 0;
  let payoffDate: string | null = null;

  const consumeRow = (row: DebtScheduleRow) => {
    if (includeScheduleRows) {
      scheduleRows.push(row);
    }
    if (row.status === 'past') {
      lastPastEndingBalance = row.endingBalance;
      if (row.rowType === 'scheduled_payment') {
        lastPastScheduledPaymentRow = row as ScheduledDebtScheduleRow;
      }
    } else if (row.rowType === 'scheduled_payment') {
      remainingTermMonths += 1;
      if (!nextPaymentRow) {
        nextPaymentRow = row as ScheduledDebtScheduleRow;
      }
    }
    if (row.rowType === 'scheduled_payment' && row.endingBalance <= 0.005 && !payoffDate) {
      payoffDate = row.date;
    }
  };

  const applyExtraEventsThrough = (cutoffDate: string, inclusive: boolean) => {
    while (eventIndex < relevantEvents.length) {
      const event = relevantEvents[eventIndex];
      const comparison = compareIsoDates(event.date, cutoffDate);
      if (comparison > 0 || (!inclusive && comparison === 0)) break;
      const beginningBalance = balance;
      const extraPrincipal = Math.max(0, Math.min(beginningBalance, Number(event.amount || 0)));
      balance = round2(Math.max(0, beginningBalance - extraPrincipal));
      consumeRow({
        id: event.id,
        rowType: 'extra_principal_payment',
        date: event.date,
        status: compareIsoDates(event.date, today) <= 0 ? 'past' : 'future',
        beginningBalance: round2(beginningBalance),
        scheduledPayment: 0,
        interestPortion: 0,
        principalPortion: 0,
        extraPrincipal: round2(extraPrincipal),
        endingBalance: round2(balance),
        notes: event.notes || 'Extra principal payment'
      });
      eventIndex += 1;
    }
  };

  for (let month = 0; month < maxSchedulePayments && balance > 0.005; month += 1) {
    const paymentDateIso = toIsoDate(paymentDate);
    applyExtraEventsThrough(paymentDateIso, false);

    const beginningBalance = round2(balance);
    const monthlyRate = Math.max(0, debt.interestRate || 0) / 100 / 12;
    const interestPortion = round2(beginningBalance * monthlyRate);
    const scheduledPayment = getScheduledPaymentAmount(debt, beginningBalance, paymentIndex);
    const principalPortion = debt.loanType === 'line_of_credit' || debt.loanType === 'interest_only' || (debt.loanType === 'balloon' && debt.amortYears <= 0)
      ? 0
      : round2(Math.max(0, Math.min(beginningBalance, scheduledPayment - interestPortion)));
    balance = round2(Math.max(0, beginningBalance - principalPortion));

    consumeRow({
      id: `${debt.id}-payment-${paymentIndex + 1}`,
      rowType: 'scheduled_payment',
      date: paymentDateIso,
      status: compareIsoDates(paymentDateIso, today) <= 0 ? 'past' : 'future',
      beginningBalance,
      scheduledPayment: round2(scheduledPayment),
      interestPortion,
      principalPortion,
      extraPrincipal: 0,
      endingBalance: round2(balance),
      notes: 'Scheduled payment'
    });

    applyExtraEventsThrough(paymentDateIso, true);
    paymentIndex += 1;
    paymentDate = addMonthsWithDay(firstPaymentDate, paymentIndex, paymentDay);

    if (debt.balloonDate && compareIsoDates(paymentDateIso, debt.balloonDate) >= 0) {
      break;
    }
  }

  applyExtraEventsThrough('9999-12-31', true);

  const pastExtraPrincipal = relevantEvents
    .filter((event) => compareIsoDates(event.date, today) <= 0)
    .reduce((sum, event) => sum + Math.max(0, Number(event.amount || 0)), 0);
  const currentBalance = debt.assumeScheduledPaymentsMade
    ? round2(lastPastEndingBalance ?? debt.originalBalance)
    : round2(Math.max(0, debt.originalBalance - pastExtraPrincipal));
  // Payoff should be driven by the live schedule so ad hoc principal payments
  // can shorten the loan naturally instead of being capped by a manual maturity date.
  const estimatedPayoffDate = debt.balloonDate || payoffDate || null;
  const nextScheduledRow = nextPaymentRow as ScheduledDebtScheduleRow | null;
  const lastPastScheduledRow = lastPastScheduledPaymentRow as ScheduledDebtScheduleRow | null;
  const nextScheduledPayment = nextScheduledRow ? nextScheduledRow.scheduledPayment : debt.monthlyPayment;
  const nextScheduledPaymentDate = nextScheduledRow ? nextScheduledRow.date : null;
  const nextScheduledInterestPortion = nextScheduledRow ? nextScheduledRow.interestPortion : null;
  const nextScheduledPrincipalPortion = nextScheduledRow ? nextScheduledRow.principalPortion : null;
  const lastPastInterestPortion = lastPastScheduledRow ? lastPastScheduledRow.interestPortion : null;
  const lastPastPrincipalPortion = lastPastScheduledRow ? lastPastScheduledRow.principalPortion : null;
  const liveDebt = {
    ...debt,
    currentBalance,
    monthlyPayment: round2(nextScheduledPayment)
  };

  return {
    debt: liveDebt,
    scheduleRows,
    currentBalance,
    scheduledMonthlyPayment: round2(nextScheduledPayment),
    nextPaymentDate: nextScheduledPaymentDate,
    currentInterestPortion: round2(nextScheduledInterestPortion ?? lastPastInterestPortion ?? 0),
    currentPrincipalPortion: round2(nextScheduledPrincipalPortion ?? lastPastPrincipalPortion ?? 0),
    remainingTermMonths,
    estimatedPayoffDate
  };
};

export const withCalculatedDebtPayment = (debt: DebtRecord, events: DebtEvent[] = []): DebtRecord => (
  buildDebtLiveSnapshot(debt, events).debt
);

export const calculateCurrentDebtBreakdown = (
  debt: Pick<DebtRecord, 'id' | 'currentBalance' | 'interestRate' | 'monthlyPayment' | 'loanType' | 'originalBalance' | 'amortYears' | 'interestOnly' | 'interestOnlyMonths' | 'originationDate' | 'firstPaymentDate' | 'paymentDayOfMonth' | 'paymentFrequency' | 'assumeScheduledPaymentsMade' | 'maturityDate' | 'balloonDate' | 'balloonAmount' | 'creditLimit' | 'name' | 'lender' | 'notes' | 'createdAt' | 'updatedAt'>,
  events: DebtEvent[] = []
) => {
  const snapshot = buildDebtLiveSnapshot(debt, events);
  return {
    interestPortion: snapshot.currentInterestPortion,
    principalPortion: snapshot.currentPrincipalPortion
  };
};

const getPropertyAllocationValue = (
  propertyId: string,
  properties: Record<string, Property>,
  valueBasis: AllocationValueBasis = 'purchase_price',
  manualValueOverride?: number | null,
  modelResults?: Record<string, { assumedSalePrice?: number }>
): number => {
  const property = resolvePropertyRecord(propertyId, properties);
  if (!property) return 0;
  const propertyInputs = property.currentYearInputs || property.inputs;
  if (valueBasis === 'manual') return Math.max(0, Number(manualValueOverride || 0));
  if (valueBasis === 'current_value') return Math.max(0, modelResults?.[propertyId]?.assumedSalePrice || propertyInputs.purchasePrice || 0);
  return Math.max(0, propertyInputs.purchasePrice || 0);
};

const getPropertyAllocationWeight = (
  link: PropertyDebtLink,
  properties: Record<string, Property>,
  modelResults?: Record<string, { assumedSalePrice?: number }>
): number => {
  if (link.allocationMethod === 'equal_split') return 1;
  if (link.allocationMethod === 'current_value') {
    return getPropertyAllocationValue(link.propertyId, properties, 'current_value', null, modelResults);
  }
  if (link.allocationMethod === 'normalize_ltv') {
    return getPropertyAllocationValue(link.propertyId, properties, link.valueBasis || 'purchase_price', link.manualValueOverride, modelResults);
  }
  return getPropertyAllocationValue(link.propertyId, properties, 'purchase_price', null, modelResults);
};

const getEligibleDebtLinks = (
  debt: DebtRecord,
  linksForDebt: PropertyDebtLink[],
  properties: Record<string, Property>
) => {
  const normalizedEntity = (debt.ownershipEntity || '').trim().toLowerCase();
  if (!normalizedEntity) return linksForDebt;
  return linksForDebt.filter((link) => {
    const property = resolvePropertyRecord(link.propertyId, properties);
    return (property?.ownershipEntity || '').trim().toLowerCase() === normalizedEntity;
  });
};

const getNormalizedLtvContext = (
  debt: DebtRecord,
  links: PropertyDebtLink[],
  properties: Record<string, Property>,
  debts: Record<string, DebtRecord>,
  debtEvents: DebtEvent[] = [],
  modelResults?: Record<string, { assumedSalePrice?: number }>
) => {
  const relevantLinks = getEligibleDebtLinks(debt, links.filter((link) => {
    const candidateDebt = debts[link.debtId];
    if (!candidateDebt) return false;
    if (!candidateDebt.isSharedPortfolioDebt) return false;
    if ((candidateDebt.ownershipEntity || '').trim().toLowerCase() !== (debt.ownershipEntity || '').trim().toLowerCase()) return false;
    return link.allocationMethod === 'normalize_ltv';
  }), properties);
  const basis = relevantLinks[0]?.valueBasis || 'purchase_price';
  const uniquePropertyLinks = new Map<string, PropertyDebtLink>();
  relevantLinks.forEach((link) => {
    if (!uniquePropertyLinks.has(link.propertyId)) uniquePropertyLinks.set(link.propertyId, link);
  });
  const totalPortfolioValue = [...uniquePropertyLinks.values()].reduce((sum, link) => (
    sum + getPropertyAllocationValue(link.propertyId, properties, basis, link.manualValueOverride, modelResults)
  ), 0);
  const debtIds = [...new Set(relevantLinks.map((link) => link.debtId))];
  const totalPortfolioDebt = debtIds.reduce((sum, debtId) => {
    const candidateDebt = debts[debtId];
    if (!candidateDebt) return sum;
    return sum + withCalculatedDebtPayment(candidateDebt, debtEvents.filter((event) => event.debtId === debtId)).currentBalance;
  }, 0);

  return {
    totalPortfolioValue: round2(totalPortfolioValue),
    totalPortfolioDebt: round2(totalPortfolioDebt),
    portfolioLtv: totalPortfolioValue > 0 ? totalPortfolioDebt / totalPortfolioValue : 0
  };
};

export const resolveDebtAllocationPercent = (
  link: PropertyDebtLink,
  linksForDebt: PropertyDebtLink[],
  properties: Record<string, Property>,
  modelResults?: Record<string, { assumedSalePrice?: number }>,
  debt?: DebtRecord,
  debts?: Record<string, DebtRecord>,
  debtEvents: DebtEvent[] = []
): number => {
  if (link.allocationMethod === 'manual') return Math.max(0, link.allocationPercent || 0);
  const eligibleLinks = debt ? getEligibleDebtLinks(debt, linksForDebt, properties) : linksForDebt;
  if (link.allocationMethod === 'normalize_ltv' && debt && debts) {
    const context = getNormalizedLtvContext(debt, eligibleLinks, properties, debts, debtEvents, modelResults);
    if (context.totalPortfolioValue <= 0 || context.totalPortfolioDebt <= 0) return 0;
  }
  const weights = eligibleLinks.map((candidate) => getPropertyAllocationWeight(candidate, properties, modelResults));
  const totalWeight = weights.reduce((sum, value) => sum + value, 0);
  if (totalWeight <= 0) return 0;
  const linkWeight = getPropertyAllocationWeight(link, properties, modelResults);
  return round2((linkWeight / totalWeight) * 100);
};

export interface ResolvedDebtLinkAllocation {
  link: PropertyDebtLink;
  debt: DebtRecord;
  property: Property | null;
  allocationPercent: number;
  allocationValue: number;
  valueBasis: AllocationValueBasis;
  portfolioLtv: number;
  allocatedOriginalLoanAmount: number;
  allocatedCurrentBalance: number;
  allocatedMonthlyPayment: number;
}

export const resolveDebtLinksForDebt = (
  debtId: string,
  links: PropertyDebtLink[],
  properties: Record<string, Property>,
  debts: Record<string, DebtRecord>,
  debtEvents: DebtEvent[] = [],
  modelResults?: Record<string, { assumedSalePrice?: number }>
): ResolvedDebtLinkAllocation[] => {
  const debt = debts[debtId];
  if (!debt) return [];
  const relevant = getEligibleDebtLinks(debt, links.filter((link) => link.debtId === debtId), properties);
  const normalizedLtvContext = getNormalizedLtvContext(debt, links, properties, debts, debtEvents, modelResults);
  return relevant
    .map((link) => {
      const property = resolvePropertyRecord(link.propertyId, properties);
      const allocationPercent = resolveDebtAllocationPercent(link, relevant, properties, modelResults, debt, debts, debtEvents);
      const allocationRatio = allocationPercent / 100;
      const normalizedDebt = withCalculatedDebtPayment(debt, debtEvents);
      const valueBasis = link.valueBasis || 'purchase_price';
      const allocationValue = getPropertyAllocationValue(link.propertyId, properties, valueBasis, link.manualValueOverride, modelResults);

      return {
        link,
        debt: normalizedDebt,
        property,
        allocationPercent,
        allocationValue: round2(allocationValue),
        valueBasis,
        portfolioLtv: normalizedLtvContext.portfolioLtv,
        allocatedOriginalLoanAmount: round2(normalizedDebt.originalBalance * allocationRatio),
        allocatedCurrentBalance: round2(normalizedDebt.currentBalance * allocationRatio),
        allocatedMonthlyPayment: round2(normalizedDebt.monthlyPayment * allocationRatio)
      };
    })
    .filter(Boolean) as ResolvedDebtLinkAllocation[];
};

export const buildDebtSnapshotForProperty = (
  propertyId: string,
  properties: Record<string, Property>,
  debts: Record<string, DebtRecord>,
  links: PropertyDebtLink[],
  debtEvents: DebtEvent[] = [],
  modelResults?: Record<string, { assumedSalePrice?: number }>,
  options?: BuildDebtSnapshotOptions
): DebtAllocationSnapshot | null => {
  const propertySyncId = properties[propertyId]?.supabaseId ?? null;
  const propertyLinks = links.filter((link) => (
    link.propertyId === propertyId || (propertySyncId && link.propertyId === propertySyncId)
  )).filter((link) => {
    if (options?.includeSharedPortfolioDebt !== false) return true;
    const linkedDebt = debts[link.debtId];
    return linkedDebt ? !linkedDebt.isSharedPortfolioDebt : true;
  });
  if (!propertyLinks.length) return null;

  const resolvedAllocations = propertyLinks.flatMap((link) => (
    resolveDebtLinksForDebt(link.debtId, links, properties, debts, debtEvents, modelResults)
      .filter((entry) => entry.link.propertyId === propertyId || (propertySyncId && entry.link.propertyId === propertySyncId))
      .map((entry) => ({
        debt: entry.debt,
        link: entry.link,
        allocationPercent: entry.allocationPercent,
        allocationValue: entry.allocationValue,
        valueBasis: entry.valueBasis,
        portfolioLtv: entry.portfolioLtv,
        allocatedOriginalLoanAmount: entry.allocatedOriginalLoanAmount,
        allocatedCurrentBalance: entry.allocatedCurrentBalance,
        monthlyPayment: entry.allocatedMonthlyPayment
      }))
  ));

  if (!resolvedAllocations.length) return null;

  let maxRemainingTermMonths = 0;

  const annualDebtServiceByYear = resolvedAllocations.reduce((yearMap, entry) => {
    const allocationRatio = entry.allocationPercent / 100;
    const debtEventsForLoan = debtEvents.filter((event) => event.debtId === entry.debt.id);
    const debtSnapshot = buildDebtLiveSnapshot(entry.debt, debtEventsForLoan);
    maxRemainingTermMonths = Math.max(maxRemainingTermMonths, debtSnapshot.remainingTermMonths || 0);

    debtSnapshot.scheduleRows.forEach((row) => {
      if (row.rowType !== 'scheduled_payment') return;
      const paymentYear = Number(row.date.slice(0, 4));
      if (!Number.isFinite(paymentYear)) return;
      yearMap[paymentYear] = round2((yearMap[paymentYear] || 0) + (row.scheduledPayment * allocationRatio));
    });

    return yearMap;
  }, {} as Record<number, number>);

  const totalCurrentBalance = resolvedAllocations.reduce((sum, entry) => sum + entry.allocatedCurrentBalance, 0);
  const weightedRate = totalCurrentBalance > 0
    ? resolvedAllocations.reduce((sum, entry) => sum + (entry.debt.interestRate * entry.allocatedCurrentBalance), 0) / totalCurrentBalance
    : 0;
  const weightedAmortYears = totalCurrentBalance > 0
    ? resolvedAllocations.reduce((sum, entry) => sum + (entry.debt.amortYears * entry.allocatedCurrentBalance), 0) / totalCurrentBalance
    : 30;
  const propertyValueForLtv = propertyLinks.reduce((sum, link) => {
    const valueBasis = link.valueBasis || 'purchase_price';
    return Math.max(sum, getPropertyAllocationValue(propertyId, properties, valueBasis, link.manualValueOverride, modelResults));
  }, 0);
  const allocatedAnnualDebtService = Object.values(annualDebtServiceByYear).reduce((sum, value) => sum + value, 0);

  return {
    debtId: resolvedAllocations.map((entry) => entry.debt.id).join(','),
    debtName: resolvedAllocations.length === 1 ? resolvedAllocations[0].debt.name : `${resolvedAllocations.length} linked debts`,
    lender: resolvedAllocations.length === 1 ? resolvedAllocations[0].debt.lender : 'Multiple lenders',
    allocationPercent: round2(resolvedAllocations.reduce((sum, entry) => sum + entry.allocationPercent, 0)),
    allocationMethod: resolvedAllocations.length === 1 ? resolvedAllocations[0].link.allocationMethod : 'manual',
    valueBasis: resolvedAllocations.length === 1 ? resolvedAllocations[0].valueBasis : 'purchase_price',
    allocatedOriginalLoanAmount: round2(resolvedAllocations.reduce((sum, entry) => sum + entry.allocatedOriginalLoanAmount, 0)),
    allocatedCurrentBalance: round2(totalCurrentBalance),
    interestRate: round2(weightedRate),
    amortYears: round2(weightedAmortYears),
    remainingTermMonths: maxRemainingTermMonths,
    interestOnly: resolvedAllocations.some((entry) => entry.debt.interestOnly),
    interestOnlyMonths: Math.max(...resolvedAllocations.map((entry) => entry.debt.interestOnlyMonths || 0)),
    monthlyPayment: round2(resolvedAllocations.reduce((sum, entry) => sum + entry.monthlyPayment, 0)),
    notes: resolvedAllocations.map((entry) => entry.debt.notes).filter(Boolean).join(' | '),
    annualDebtServiceByYear,
    impliedLtv: propertyValueForLtv > 0 ? round2((totalCurrentBalance / propertyValueForLtv) * 100) : 0,
    allocatedAnnualDebtService: round2(allocatedAnnualDebtService),
    includesSharedPortfolioDebt: resolvedAllocations.some((entry) => entry.debt.isSharedPortfolioDebt)
  };
};

export const buildLegacyDebtSnapshot = (inputs: FinancialInputs): DebtAllocationSnapshot | null => {
  const loanAmount = Math.max(0, (inputs.purchasePrice || 0) - (inputs.downPayment || 0));
  if (loanAmount <= 0) return null;

  return {
    debtId: 'legacy-property-debt',
    debtName: 'Legacy Property Debt',
    lender: 'Property-level',
    allocationPercent: 100,
    allocationMethod: 'manual',
    allocatedOriginalLoanAmount: loanAmount,
    allocatedCurrentBalance: loanAmount,
    interestRate: inputs.interestRate || 0,
    amortYears: inputs.amortYears || 30,
    remainingTermMonths: Math.max(1, Math.round(inputs.amortYears || 30)) * 12,
    interestOnly: false,
    interestOnlyMonths: 0,
    monthlyPayment: calculateMonthlyPayment(loanAmount, inputs.interestRate || 0, inputs.amortYears || 30),
    notes: 'Fallback legacy debt stored directly on the property.'
  };
};

export const derivePropertyHoldPeriodYears = (debtSnapshot?: DebtAllocationSnapshot | null): number => {
  const remainingTermMonths = Math.max(0, Math.round(debtSnapshot?.remainingTermMonths || 0));
  if (remainingTermMonths > 0) {
    return Math.max(1, Math.ceil(remainingTermMonths / 12));
  }
  return DEFAULT_PROPERTY_HOLD_YEARS;
};

export const createDebtEvent = (debtId: string, type: DebtEventType = 'extra_principal_payment'): DebtEvent => {
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    debtId,
    date: todayIso(),
    type,
    amount: 0,
    notes: '',
    createdAt: now,
    updatedAt: now
  };
};
